import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { PrivacyRequest } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useUpdatePrivacyRequest } from '../hooks/use-update-privacy-request';
import { allowedFromConflict } from '../schemas/privacy-request-rules';

export interface PrivacyRequestActionDialogProps {
  storeId: string;
  request: PrivacyRequest;
  move: 'completed' | 'rejected';
  onOpenChange: (open: boolean) => void;
}

/**
 * Completing carries the request out (and a deletion cannot be undone); rejecting closes it for
 * good. The copy says exactly what completing does for each kind.
 */
export function PrivacyRequestActionDialog({
  storeId,
  request,
  move,
  onOpenChange,
}: PrivacyRequestActionDialogProps) {
  const { t } = useTranslation(['privacyRequests', 'common']);
  const message = useErrorMessage();
  const update = useUpdatePrivacyRequest(
    storeId,
    move === 'rejected' ? 'privacyRequests:toasts.rejected' : 'privacyRequests:toasts.completed',
  );
  const allowed = allowedFromConflict(update.error);
  const copy =
    move === 'rejected'
      ? 'privacyRequests:confirm.reject'
      : `privacyRequests:confirm.${request.kind}`;
  const destructive = move === 'completed' && request.kind === 'delete';

  return (
    <AlertDialog
      open
      onOpenChange={(next) => {
        if (!next) update.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`${copy}.title`, { email: request.email })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(`${copy}.description`, {
              email: request.email,
              kind: t(`privacyRequests:kind.${request.kind}`),
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {update.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {allowed
                ? allowed.length > 0
                  ? t('privacyRequests:errors.moveRefused', {
                      allowed: allowed.map((m) => t(`privacyRequests:status.${m}`)).join(', '),
                    })
                  : t('privacyRequests:errors.closed')
                : message(update.error)}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={update.isPending}>
            {t('common:actions.back')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={update.isPending}
            variant={destructive ? 'destructive' : 'default'}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              update.mutate(
                { id: request.id, body: { status: move } },
                { onSuccess: () => onOpenChange(false) },
              );
            }}
          >
            {t(`${copy}.submit`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
